import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import {
  TableContainer,
  Paper,
  Grid,
  TextField,
  Box,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  StyledButton,
  StyledSnackbar,
  StyledAlert,
  inputStyle,
  StyledButtonC,
  StyledTypography,
  StyledTableContainer,
  StyledAgGridContainer,
  ViewButton,
  DeleteButton,
} from "components/StyledComponents/styledComponents.js";
import PropTypes from "prop-types";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import ResponsiveTabs from "components/StyledComponents/styledComponents";
import Cookie from "js-cookie";
import URLs from "constants/urls";
import { useMaterialUIController } from "context";

export default function SalaryAdvance() {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const hrms_token = Cookie.get("hrms_token");

  // State variables
  const [employeeName, setEmployeeName] = useState("Loading...");
  const [hiringDate, setHiringDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [existingAdvance, setExistingAdvance] = useState("0.00");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [employeeRecordId, setEmployeeRecordId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [newAdvance, setNewAdvance] = useState("");
  const [deductionAmount, setDeductionAmount] = useState("");
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [activeTab, setActiveTab] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const MInput = inputStyle();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [remarksDialogOpen, setRemarksDialogOpen] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState("");
  const [remarks, setRemarks] = useState("");

  const handleOpenDialog = (rowData) => {
    setSelectedRow(rowData);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
  };

  const tabs = [{ label: "Salary Advances" }, { label: "Request Advance" }];

  const fetchEmployeeDetails = async (employeeId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${URLs.getEmpPayRollData}${employeeId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch employee details");
      }
      const data = await response.json();
      if (data) {
        // Always use the employee name from user context
        const employeeNameFromUser = user?.employeeName || "";
        setEmployeeName(employeeNameFromUser);
        setHiringDate(data.hiringDate || "");
        setEmployeeRecordId(data.employeeRecordId);
        return {
          recordId: data.employeeRecordId,
          employeeName: employeeNameFromUser, // Return the name from user context
        };
      }
      return null;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvanceDetails = async (employeeRecordId) => {
    if (!employeeRecordId) return;

    setLoading(true);
    try {
      // Fetch existing advance using new API
      const response = await fetch(`${URLs.hrmsExistingAdvanceUrl}/${employeeRecordId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch advance details");
      }

      const data = await response.json();
      const currentBalance = parseFloat(data || 0);
      setExistingAdvance(currentBalance > 0 ? currentBalance.toFixed(2) : "0.00");
    } catch (error) {
      console.error("Error fetching advance details:", error);
      setError("Failed to fetch advance details");
      setExistingAdvance("0.00");
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvanceList = async (recordId = employeeRecordId) => {
    if (!recordId) return;

    try {
      const response = await fetch(`${URLs.hrmsEmployeeAdvanceDetailsUrl}/${recordId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch advance list");
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        console.error("Invalid data format received:", data);
        setTableData([]);
        return;
      }

      const updatedData = data.map((item) => ({
        advanceId: item.advanceId,
        employeeRecordId: item.employeeRecordId,
        employeeName: user?.employeeName || "",
        date: formatDate(item.date),
        advanceAmount: formatNumberWithCommas(parseFloat(item.advanceAmount || 0).toFixed(2)),
        deductionAmount: formatNumberWithCommas(parseFloat(item.deductionAmount || 0).toFixed(2)),
        agreedDeductionAmount: formatNumberWithCommas(
          parseFloat(item.agreedDeductionAmount || 0).toFixed(2)
        ),
        advanceApprovedStatus: item.advanceApprovedStatus || "P",
        remarks: item.remarks || "No remarks available",
        status:
          item.advanceApprovedStatus === "P"
            ? "Pending"
            : item.advanceApprovedStatus === "A"
            ? "Approved"
            : item.advanceApprovedStatus === "R"
            ? "Rejected"
            : "Unknown",
      }));

      setTableData(updatedData);
    } catch (error) {
      console.error("Error fetching advance list:", error);
      setAlert("Failed to fetch advance list", "error");
      setTableData([]); // Set empty array on error
    }
  };

  const validateDeductionAmount = (value, advanceAmount) => {
    const deduction = parseFloat(value) || 0;
    const advance = parseFloat(advanceAmount) || 0;

    // Check if deduction is greater than advance
    if (deduction > advance && deduction <= 0) {
      return "Invalid deduction: must be positive and exceed advance";
    }
    return ""; // No error
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!employeeRecordId) {
      setAlert("Employee Record ID is missing", "error");
      return;
    }

    if (!editMode && parseFloat(existingAdvance) > 0) {
      setAlert("An existing advance already exists. New advance cannot be created.", "error");
      return;
    }

    const errorMessage = validateDeductionAmount(deductionAmount, newAdvance);
    if (errorMessage) {
      setAlert(errorMessage, "error");
      return;
    }
    const advanceAmount = parseFloat(newAdvance) || 0;
    const monthlyDeduction = parseFloat(deductionAmount) || 0;
    const currentBalance = parseFloat(existingAdvance) || 0;

    const postData = {
      advanceId: editMode ? editItemId : 0,
      employeeRecordId: parseInt(employeeRecordId, 10),
      employeeName: user?.employeeName || "",
      companyId: user.companyId,
      date: new Date(selectedDate).toISOString(),
      advanceAmount: advanceAmount,
      deductionAmount: editMode ? existingAdvance : 0,
      agreedDeductionAmount: monthlyDeduction,
      advanceApprovedStatus: "P", // Single character for char data type
      approvedManagerCode: "string",
      existingBalance: currentBalance,
      remarks: remarks || "Salary Advance Request",
    };

    try {
      setSubmitting(true);
      console.log("Sending payload:", JSON.stringify(postData, null, 2)); // Pretty print the payload

      const response = await fetch(
        editMode ? `${URLs.updateAdvanceDetails}/${editItemId}` : URLs.grantAdvance,
        {
          method: editMode ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${hrms_token}`,
          },
          body: JSON.stringify(postData),
        }
      );

      const responseText = await response.text();
      console.log("Response:", responseText);

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || "Server error occurred";
        } catch {
          errorMessage = responseText || `Failed to ${editMode ? "update" : "submit"} advance`;
        }
        throw new Error(errorMessage);
      }

      // Clear form data
      resetForm();

      // Reset all relevant state but keep employee name from user context
      setNewAdvance("");
      setDeductionAmount("");
      if (!user?.employeeId) {
        setEmployeeName("");
        setHiringDate("");
        setEmployeeRecordId(null);
      } else {
        setEmployeeName(user.employeeName || "");
      }

      // Refresh the advance list and existing balance
      await Promise.all([
        fetchAdvanceList(employeeRecordId),
        fetchAdvanceDetails(employeeRecordId),
      ]);

      setAlert(`Advance successfully ${editMode ? "updated" : "submitted"}`, "success");
      setActiveTab(0);
    } catch (error) {
      console.error("Submission error:", error);
      setAlert(
        error.message || `Failed to ${editMode ? "update" : "submit"} advance details`,
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setNewAdvance("");
    setDeductionAmount("");
    setRemarks(""); // Reset remarks
    setSelectedCompany(null);
    setSelectedDate(new Date().toISOString().split("T")[0]);
    if (!user?.employeeId) {
      setSelectedEmployee(null);
      setEmployeeName("");
      setHiringDate("");
      setEmployeeRecordId(null);
      setExistingAdvance("0.00");
    } else {
      // Keep employee name from user context when resetting
      setEmployeeName(user.employeeName || "");
      // Refresh existing advance for the current employee
      if (employeeRecordId) {
        fetchAdvanceDetails(employeeRecordId);
      }
    }
    setEditMode(false);
    setEditItemId(null);
  };

  const handleEdit = async (rowData) => {
    setEditMode(true);
    setEditItemId(rowData.advanceId);
    const dateObj = new Date(rowData.date);
    setSelectedDate(dateObj.toISOString().split("T")[0]);
    const company = companyOptions.find((comp) => comp.id === rowData.companyId);
    setSelectedCompany(company || null);
    const employeeNameFromUser = user?.employeeName || "";
    setSelectedEmployee({
      recordId: rowData.employeeRecordId,
      employeeName: employeeNameFromUser,
    });

    setEmployeeName(employeeNameFromUser);
    const currentBalance = parseFloat(rowData.existingBalance || 0);
    setExistingAdvance(currentBalance > 0 ? currentBalance.toFixed(2) : "0");

    setNewAdvance(rowData.advanceAmount?.toString() || "0");
    setDeductionAmount(rowData.agreedDeductionAmount?.toString() || "0");
    setRemarks(rowData.remarks || ""); // Set remarks when editing
    setEmployeeRecordId(rowData.employeeRecordId);
    setActiveTab(1);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;

    try {
      const response = await fetch(`${URLs.deleteAdvance}/${selectedRow.advanceId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete advance");
      }

      setAlert("Advance deleted successfully", "success");
      await fetchAdvanceList();
      setExistingAdvance(0);
    } catch (error) {
      console.error("Delete error:", error);
      setAlert("Failed to delete advance", "error");
    } finally {
      handleCloseDialog();
    }
  };

  const handleChange = (event, newValue) => {
    // Reset form if switching away from Request Advance tab
    if (activeTab === 1) {
      resetForm();
    }
    setActiveTab(newValue);
  };

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatNumberWithCommas = (value) => {
    // Remove any existing commas and non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, "");
    // Format with commas
    const parts = numericValue.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const handleNewAdvanceChange = (e) => {
    const rawValue = e.target.value.replace(/[^\d.]/g, "");
    setNewAdvance(rawValue);
  };

  const handleDeductionAmountChange = (e) => {
    const rawValue = e.target.value.replace(/[^\d.]/g, "");
    setDeductionAmount(rawValue);
    const errorMessage = validateDeductionAmount(rawValue, newAdvance);
    if (errorMessage) {
      setAlert(errorMessage, "error");
    }
  };

  const ViewRemarksRenderer = (props) => (
    <div style={{ cursor: "pointer", display: "flex", justifyContent: "center" }}>
      <Tooltip title="View Remarks">
        <IconButton
          onClick={() => {
            setSelectedRemarks(props.value || "No remarks available");
            setRemarksDialogOpen(true);
          }}
          size="small"
        >
          <VisibilityIcon style={{ color: "#1a73e8" }} />
        </IconButton>
      </Tooltip>
    </div>
  );

  ViewRemarksRenderer.propTypes = {
    value: PropTypes.string,
  };

  ViewRemarksRenderer.defaultProps = {
    value: "",
  };

  const ActionCellRenderer = (props) => {
    const rowData = props.data;

    // Don't show edit/delete buttons if status is Approved
    if (rowData.advanceApprovedStatus === "A") {
      return null;
    }

    return (
      <div>
        <ViewButton onClick={() => handleEdit(rowData)}>
          <EditIcon />
        </ViewButton>
        <DeleteButton onClick={() => handleOpenDialog(rowData)}>
          <DeleteIcon />
        </DeleteButton>
      </div>
    );
  };

  ActionCellRenderer.propTypes = {
    data: PropTypes.object.isRequired,
  };

  ViewRemarksRenderer.propTypes = {
    value: PropTypes.string,
  };

  const columnDefs = [
    {
      headerName: "Date",
      field: "date",
      sortable: true,
      sort: "desc",
      comparator: (date1, date2) => new Date(date2) - new Date(date1),
      filter: true,
      flex: 1,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Employee Name",
      field: "employeeName",
      sortable: true,
      filter: true,
      flex: 1,
    },

    {
      headerName: "Advance Amount",
      field: "advanceAmount",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => {
        return `₹ ${formatNumberWithCommas(params.value)}`;
      },
    },
    {
      headerName: "Deduction per Month",
      field: "agreedDeductionAmount",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => {
        return `₹ ${formatNumberWithCommas(params.value)}`;
      },
    },
    {
      headerName: "Remarks",
      field: "remarks",
      cellRenderer: ViewRemarksRenderer,
      width: 100,
      cellStyle: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
    },
    {
      headerName: "Status",
      field: "advanceApprovedStatus",
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: (params) => {
        const status = params.value;
        if (!status) return "PENDING";
        switch (status) {
          case "P":
            return "PENDING";
          case "A":
            return "APPROVED";
          case "R":
            return "REJECTED";
          default:
            return "PENDING";
        }
      },
      cellStyle: (params) => {
        const status = params.value;
        if (!status) return { color: "#ED6C02" };
        return {
          color: status === "A" ? "#2E7D32" : status === "R" ? "#D32F2F" : "#ED6C02",
          fontWeight: "500",
        };
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: ActionCellRenderer,
      width: 150,
      sortable: false,
      filter: false,
    },
  ];

  useEffect(() => {
    const initializeData = async () => {
      if (user) {
        // Set employee name from user context
        const employeeNameFromUser = user.employeeName || "Loading...";
        setEmployeeName(employeeNameFromUser);

        if (user.employeeId) {
          try {
            const employeeData = await fetchEmployeeDetails(user.employeeId);
            if (employeeData?.recordId) {
              setSelectedEmployee({
                recordId: employeeData.recordId,
                employeeName: employeeNameFromUser, // Use the name from user context
              });
              setEmployeeRecordId(employeeData.recordId);

              // Fetch both advance details and list in parallel
              await Promise.all([
                fetchAdvanceDetails(employeeData.recordId),
                fetchAdvanceList(employeeData.recordId),
              ]);
            }
          } catch (error) {
            console.error("Error initializing data:", error);
            setAlert("Failed to load employee data", "error");
          }
        }
      }
    };

    initializeData();
  }, [user]);

  return (
    <DashboardLayout>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          mt: 0,
          pt: 1,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <ResponsiveTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleChange}
          labelColor="secondary"
          bgColor="#e0f7fa"
        />
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            mt: 2,
            pt: 1,
            backgroundColor: "transparent",
            boxShadow: "none",
            border: "none",
          }}
        >
          {activeTab === 0 && (
            <Box
              sx={{
                overflowX: "auto",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                mt: 2,
              }}
            >
              <StyledAgGridContainer
                component={Paper}
                className={`ag-theme-alpine`}
                sx={{
                  flexGrow: 1,
                  height: "100%",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  border: "none",
                }}
              >
                <AgGridReact
                  rowData={tableData}
                  columnDefs={columnDefs}
                  pagination={true}
                  paginationPageSize={11}
                />
              </StyledAgGridContainer>
            </Box>
          )}
          {activeTab === 1 && (
            <StyledTableContainer
              container
              spacing={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#ffffff",
                height: "100%",
                flexGrow: 1,
                mt: 0,
                borderRadius: "15px",
              }}
            >
              <Grid
                container
                spacing={1}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Grid item xs={12} md={12} lg={10}>
                  <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                    <div style={{ position: "relative", width: "100%" }}>
                      <StyledTypography>
                        {editMode ? "Edit Salary Advance" : "Salary Advance"}
                      </StyledTypography>
                      {editMode && (
                        <Tooltip title="Reset">
                          <StyledButtonC onClick={resetForm}>
                            <RestartAltIcon />
                          </StyledButtonC>
                        </Tooltip>
                      )}
                    </div>
                    <Grid sx={{ padding: 3 }}>
                      <form onSubmit={handleSubmit} autoComplete="off">
                        <Grid container spacing={4}>
                          {/* Left Column */}
                          <Grid item xs={12} md={6}>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  label="Name"
                                  value={employeeName}
                                  fullWidth
                                  disabled
                                  sx={MInput.b}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  label="Date"
                                  type="date"
                                  value={selectedDate}
                                  onChange={(e) => setSelectedDate(e.target.value)}
                                  sx={{
                                    height: 43,
                                    ...MInput.b,
                                    "& .MuiInputBase-root": { height: "100%", minHeight: 43 },
                                  }}
                                  InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  label="Hiring Date"
                                  value={formatDate(hiringDate) || ""}
                                  fullWidth
                                  disabled
                                  sx={MInput.b}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  label="Existing Advance"
                                  value={existingAdvance}
                                  fullWidth
                                  disabled
                                  sx={MInput.b}
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          {/* Right Column */}
                          <Grid item xs={12} md={6}>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <TextField
                                  label="New Advance"
                                  value={newAdvance}
                                  onChange={handleNewAdvanceChange}
                                  fullWidth
                                  required
                                  sx={MInput.b}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">₹</InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  label="Deduction per Month"
                                  value={deductionAmount}
                                  onChange={handleDeductionAmountChange}
                                  fullWidth
                                  required
                                  // error={!!validateDeductionAmount(deductionAmount, newAdvance)}
                                  // helperText={validateDeductionAmount(deductionAmount, newAdvance)}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">₹</InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    ...MInput.b,
                                    "& .MuiFormHelperText-root": {
                                      color: "error.main",
                                      marginLeft: 0,
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  label="Remarks"
                                  value={remarks}
                                  onChange={(e) => setRemarks(e.target.value)}
                                  fullWidth
                                  multiline
                                  rows={3}
                                  sx={MInput.b}
                                />
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid item xs={12}>
                            <Box
                              sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
                            >
                              <StyledButton
                                onClick={() => {
                                  resetForm();
                                  setActiveTab(0);
                                }}
                                color="primary"
                              >
                                Close
                              </StyledButton>
                              <StyledButton
                                type="submit"
                                disabled={
                                  parseFloat(deductionAmount) > parseFloat(newAdvance) ||
                                  loading ||
                                  submitting
                                }
                              >
                                {submitting ? "Processing..." : editMode ? "Update" : "Submit"}
                              </StyledButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </form>
                    </Grid>
                  </TableContainer>
                </Grid>
              </Grid>
            </StyledTableContainer>
          )}
        </Box>
      </Box>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this advance?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={handleCloseDialog}>Cancel</StyledButton>
          <StyledButton onClick={handleDelete} autoFocus>
            Delete
          </StyledButton>
        </DialogActions>
      </Dialog>
      <Dialog open={remarksDialogOpen} onClose={() => setRemarksDialogOpen(false)}>
        <DialogTitle>Remarks</DialogTitle>
        <DialogContent>
          <DialogContentText>{selectedRemarks}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={() => setRemarksDialogOpen(false)}>Close</StyledButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

SalaryAdvance.propTypes = {
  data: PropTypes.object,
};
